import { useState, useCallback } from 'react';

export interface UseAccordionOptions {
    allowMultiple?: boolean;
    defaultOpen?: Array<string | number> | string | number | null;
    onToggle?: (sectionId: string | number, isOpen: boolean) => void;
    onChange?: (openSections: Array<string | number> | string | number | null) => void;
}

export const useAccordion = ({
    allowMultiple = false,
    defaultOpen = allowMultiple ? [] : null,
    onToggle,
    onChange,
}: UseAccordionOptions = {}) => {
    // Initialize state based on allowMultiple setting
    const [openSections, setOpenSections] = useState(() => {
        if (allowMultiple) {
            return Array.isArray(defaultOpen) ? defaultOpen : defaultOpen !== null ? [defaultOpen] : [];
        } else {
            return Array.isArray(defaultOpen) ? defaultOpen[0] || null : defaultOpen;
        }
    });

    const toggleSection = useCallback(
        (sectionId: string | number) => {
            setOpenSections((prev) => {
                let newState;

                if (allowMultiple && Array.isArray(prev)) {
                    // Multiple sections can be open
                    if (prev.includes(sectionId)) {
                        newState = prev.filter((id) => id !== sectionId);
                    } else {
                        newState = [...prev, sectionId];
                    }
                } else {
                    // Only one section can be open
                    newState = prev === sectionId ? null : sectionId;
                }

                // Call callbacks
                if (onToggle) {
                    onToggle(
                        sectionId,
                        allowMultiple && Array.isArray(newState) ? newState.includes(sectionId) : newState === sectionId
                    );
                }

                if (onChange) {
                    onChange(newState);
                }

                return newState;
            });
        },
        [allowMultiple, onToggle, onChange]
    );

    const openSection = useCallback(
        (sectionId: string | number) => {
            setOpenSections((prev) => {
                let newState;

                if (allowMultiple && Array.isArray(prev)) {
                    if (!prev.includes(sectionId)) {
                        newState = [...prev, sectionId];
                    } else {
                        return prev; // No change needed
                    }
                } else {
                    if (prev !== sectionId) {
                        newState = sectionId;
                    } else {
                        return prev; // No change needed
                    }
                }

                if (onChange) {
                    onChange(newState);
                }

                return newState;
            });
        },
        [allowMultiple, onChange]
    );

    const closeSection = useCallback(
        (sectionId: string | number) => {
            setOpenSections((prev) => {
                let newState;

                if (allowMultiple && Array.isArray(prev)) {
                    if (prev.includes(sectionId)) {
                        newState = prev.filter((id) => id !== sectionId);
                    } else {
                        return prev; // No change needed
                    }
                } else {
                    if (prev === sectionId) {
                        newState = null;
                    } else {
                        return prev; // No change needed
                    }
                }

                if (onChange) {
                    onChange(newState);
                }

                return newState;
            });
        },
        [allowMultiple, onChange]
    );

    /**
     * Close all sections
     */
    const closeAll = useCallback(() => {
        const newState = allowMultiple ? [] : null;
        setOpenSections(newState);

        if (onChange) {
            onChange(newState);
        }
    }, [allowMultiple, onChange]);

    const openAll = useCallback(
        (allSectionIds = []) => {
            if (!allowMultiple) {
                console.warn('openAll() only works when allowMultiple is true');
                return;
            }

            setOpenSections(allSectionIds);

            if (onChange) {
                onChange(allSectionIds);
            }
        },
        [allowMultiple, onChange]
    );

    const isOpen = useCallback(
        (sectionId: string | number) => {
            if (allowMultiple && Array.isArray(openSections)) {
                return openSections.includes(sectionId);
            } else {
                return openSections === sectionId;
            }
        },
        [openSections, allowMultiple]
    );

    const getTriggerProps = useCallback(
        (sectionId: string | number) => ({
            onClick: () => toggleSection(sectionId),
            'aria-expanded': isOpen(sectionId),
            'aria-controls': `accordion-content-${sectionId}`,
            id: `accordion-trigger-${sectionId}`,
            role: 'button',
            tabIndex: 0,
            onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSection(sectionId);
                }
            },
        }),
        [toggleSection, isOpen]
    );

    const getContentProps = useCallback(
        (sectionId: string | number) => ({
            id: `accordion-content-${sectionId}`,
            'aria-labelledby': `accordion-trigger-${sectionId}`,
            role: 'region',
            hidden: !isOpen(sectionId),
            'aria-hidden': !isOpen(sectionId),
        }),
        [isOpen]
    );

    return {
        // State
        openSections,

        // Control functions
        toggleSection,
        openSection,
        closeSection,
        closeAll,
        openAll,

        // Helper functions
        isOpen,
        getTriggerProps,
        getContentProps,

        // Computed values
        hasOpenSections: allowMultiple ? Array.isArray(openSections) && openSections.length > 0 : openSections !== null,
        openCount: allowMultiple ? Array.isArray(openSections) && openSections.length : openSections !== null ? 1 : 0,
    };
};
